import { Injectable } from '@angular/core';
import { IUser } from '@app/shared/interfaces/user.interface';
import { Apollo } from 'apollo-angular';
import { first, map, pluck, take } from 'rxjs/operators';
import { Observable, ReplaySubject } from 'rxjs';
import {
	FollowBuildingMutation,
	UnfollowBuildingMutation,
	UserPolicyAcceptedMutation,
} from '@auth/graphql/auth-graphql';
import { AVAILABLE_USER_ROLES, OrganizationTypes } from '@app/shared';
import { CurrentUserSuccess } from '@auth/state/auth.actions';
import { Store } from '@ngrx/store';
import { IAuthState } from '@auth/state/auth.reducer';
import { IOption } from '@shared/interfaces/selectable-options.interfaces';

@Injectable({
	providedIn: 'root',
})
export class AuProfileService {
	private readonly userProfile = new ReplaySubject<IUser>();
	readonly roleMap = new Map<string, string>();
	readonly userProfile$ = this.userProfile.asObservable();

	constructor(
		private apollo: Apollo,
		private store$: Store<IAuthState>
	) {
		Object.keys(AVAILABLE_USER_ROLES).forEach(role => this.roleMap.set(role, role));
	}

	// TODO refactor this and leave only one source of truth
	setUserProfile(profile: IUser): void {
		this.userProfile.next(profile);
		this.store$.dispatch(new CurrentUserSuccess({ user: profile }));
	}

	userHasRoleAsync(
		checkingRole: AVAILABLE_USER_ROLES | AVAILABLE_USER_ROLES[]
	): Observable<boolean> {
		return this.userProfile$.pipe(
			map(profile => {
				if (this.wrongRole(checkingRole)) {
					this.throwWrongRoleError(checkingRole);
				}
				return Array.isArray(checkingRole)
					? checkingRole.includes(profile?.organization?.userOrganizationRole)
					: checkingRole === profile?.organization?.userOrganizationRole;
			})
		);
	}

	getUserTeamPsIdAsync(): Observable<string> {
		return this.userProfile$.pipe(
			take(1),
			map(profile => profile.team[0].psId)
		);
	}

	policyAccepted(): Observable<{ success: boolean; profile: IUser }> {
		return this.apollo
			.mutate<{ userPolicyAccept: { success: boolean; profile: IUser } }>({
				mutation: UserPolicyAcceptedMutation,
			})
			.pipe(first(), pluck('data', 'userPolicyAccept'));
	}

	canUserAddBuilding(profile: IUser): boolean {
		const userOrganizationRole = profile.organization.userOrganizationRole;
		const organizationType = profile.organization.type;

		return (
			(userOrganizationRole === AVAILABLE_USER_ROLES.ADMIN ||
				userOrganizationRole === AVAILABLE_USER_ROLES.SUPER_ADMIN) &&
			(organizationType === OrganizationTypes.admin ||
				organizationType === OrganizationTypes.propertyManager ||
				organizationType === OrganizationTypes.facilityManager)
		);
	}

	followBuilding(building: { id: string; name: string }): Observable<IOption[]> {
		return this.apollo
			.mutate<{
				followBuilding: { success: boolean; profile: { followingBuildings: IOption[] } };
			}>({
				mutation: FollowBuildingMutation,
				variables: { buildingId: building.id },
			})
			.pipe(map(res => res.data?.followBuilding?.profile?.followingBuildings));
	}

	unfollowBuilding(buildingId: string): Observable<IOption[]> {
		return this.apollo
			.mutate<{
				unfollowBuilding: {
					success: boolean;
					profile: { followingBuildings: IOption[] };
				};
			}>({
				mutation: UnfollowBuildingMutation,
				variables: { buildingId },
			})
			.pipe(map(res => res.data?.unfollowBuilding?.profile?.followingBuildings));
	}

	private wrongRole(checkingRole): boolean {
		if (Array.isArray(checkingRole)) {
			checkingRole.every(role => this.roleMap.has(role));
		}

		return this.roleMap.has(checkingRole);
	}

	private throwWrongRoleError(role) {
		throw new Error(`Wrong format of given for check role. 
			Need to be one or more of ${JSON.stringify(Object.values(AVAILABLE_USER_ROLES))} 
			but got ${JSON.stringify(role)} instead`);
	}
}
